import {
  NumericTextBox,
  NumericTextBoxBlurEvent,
  NumericTextBoxChangeEvent,
  NumericTextBoxFocusEvent,
} from '@progress/kendo-react-inputs';
import { NumberFormatOptions } from '@progress/kendo-react-intl';
import clsx from 'clsx';
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { round } from 'src/utils/number';

interface ParNumericInputProps {
  value: number;
  onChange: (e: SyntheticEvent, value: number) => void;
  disabled?: boolean;
  className?: string;
}

const DECIMAL_PLACES = 2;
const ABSOLUTE_MAX = 9999;
const ABSOLUTE_MIN = -9999;
const VALID_INPUT_PATTERN = /^-?\d*\.?\d{0,2}$/;

const NUMBER_FORMAT: NumberFormatOptions = {
  style: 'decimal',
  useGrouping: false,
  minimumFractionDigits: 0,
  maximumFractionDigits: DECIMAL_PLACES,
};

const clampValue = (value: number, minValue: number, maxValue: number) =>
  Math.min(maxValue, Math.max(minValue, value));

const resolveValue = (value: number | null | undefined) =>
  value == null || !Number.isFinite(value) ? 0 : value;

const isSignToggleInput = (value: string) => /^-?\d*\.?\d*-$/.test(value);

const isIntermediateInput = (value: string) =>
  value === '' ||
  value === '-' ||
  value === '.' ||
  value === '-.' ||
  /^-?\d+\.$/.test(value) ||
  isSignToggleInput(value);

const isOutOfRangeInput = (candidate: string, minValue: number, maxValue: number) => {
  if (isIntermediateInput(candidate)) {
    return false;
  }
  if (!VALID_INPUT_PATTERN.test(candidate)) {
    return true;
  }
  const parsed = Number(candidate);
  return Number.isFinite(parsed) && (parsed > maxValue || parsed < minValue);
};

const ParNumericInput = ({
  value,
  onChange,
  disabled = false,
  className,
}: ParNumericInputProps) => {
  const [localValue, setLocalValue] = useState(clampValue(resolveValue(value), ABSOLUTE_MIN, ABSOLUTE_MAX));
  const [inputValue, setInputValue] = useState(() =>
    String(clampValue(resolveValue(value), ABSOLUTE_MIN, ABSOLUTE_MAX))
  );
  const [isFocused, setIsFocused] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const isEditingRef = useRef(false);
  const inputResetKey = useMemo(
    () => `${inputKey}-${ABSOLUTE_MIN}-${ABSOLUTE_MAX}`,
    [inputKey]
  );

  useEffect(() => {
    if (isEditingRef.current) {
      return;
    }
    const nextValue = clampValue(resolveValue(value), ABSOLUTE_MIN, ABSOLUTE_MAX);
    setLocalValue(nextValue);
    setInputValue(String(nextValue));
  }, [value]);

  const handleChange = useCallback(
    (event: NumericTextBoxChangeEvent) => {
      event.syntheticEvent?.stopPropagation();
      if (disabled) {
        return;
      }
      isEditingRef.current = true;
      const raw = (event.syntheticEvent?.target as HTMLInputElement | undefined)?.value ?? '';
      const nextValue = resolveValue(event.value);
      const normalized = nextValue === 0 ? 0 : clampValue(nextValue, ABSOLUTE_MIN, ABSOLUTE_MAX);
      setLocalValue(normalized);
      setInputValue(raw);
      if (
        isFocused &&
        raw &&
        !isIntermediateInput(raw) &&
        isOutOfRangeInput(raw, ABSOLUTE_MIN, ABSOLUTE_MAX)
      ) {
        setInputKey((key) => key + 1);
      }
    },
    [disabled, isFocused]
  );

  const handleFocus = useCallback(
    (event: NumericTextBoxFocusEvent) => {
      if (disabled) {
        return;
      }
      setIsFocused(true);
      event.target.element?.select();
    },
    [disabled]
  );

  const handleBlur = useCallback(
    (event: NumericTextBoxBlurEvent) => {
      setIsFocused(false);
      isEditingRef.current = false;
      const current = resolveValue(event.target.value);
      const clamped = current === 0 ? 0 : clampValue(current, ABSOLUTE_MIN, ABSOLUTE_MAX);
      setLocalValue(clamped);
      setInputValue(String(clamped));
      const committed = clampValue(resolveValue(value), ABSOLUTE_MIN, ABSOLUTE_MAX);
      if (clamped !== committed) {
        onChange?.(event.syntheticEvent, clamped);
      }
    },
    [onChange, value]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled || event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
        return;
      }
      const input = event.target as HTMLInputElement;
      if (input.getAttribute('aria-label') !== 'PAR') {
        return;
      }
      if (event.key === '-') {
        return;
      }
      const selectionStart = input.selectionStart ?? 0;
      const selectionEnd = input.selectionEnd ?? 0;
      const nextValue = `${input.value.slice(0, selectionStart)}${event.key}${input.value.slice(selectionEnd)}`;
      if (isOutOfRangeInput(nextValue, ABSOLUTE_MIN, ABSOLUTE_MAX)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [disabled]
  );

  const tooltipValue = isEditingRef.current
    ? inputValue
    : String(round(localValue, DECIMAL_PLACES));

  return (
    <div
      className={clsx(
        'flex items-center justify-start border border-gray-300 rounded-md shadow-sm',
        isFocused && !disabled && 'ring',
        disabled && 'bg-gray-100',
        !disabled && 'bg-white',
        className
      )}
      data-tooltip-id="global-tooltip"
      data-tooltip-content={tooltipValue}
      onChange={(event) => event.stopPropagation()}
      onKeyDown={handleKeyDown}
    >
      <NumericTextBox
        key={inputResetKey}
        value={localValue}
        min={ABSOLUTE_MIN}
        max={ABSOLUTE_MAX}
        step={1}
        format={NUMBER_FORMAT}
        spinners={false}
        disabled={disabled}
        inputType="text"
        fillMode={null}
        rounded={null}
        size="small"
        width="100%"
        ariaLabel="PAR"
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={clsx(
          '!border-0 !bg-transparent !shadow-none',
          disabled ? 'text-gray-400' : 'text-black'
        )}
        inputStyle={{
          textAlign: 'left',
          background: 'transparent',
          border: 0,
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
    </div>
  );
};

export default ParNumericInput;
